import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ContentEditor from './ContentEditor';
import { FiPackage, FiFileText, FiMail, FiImage, FiMenu, FiX, FiHome } from 'react-icons/fi';

/**
 * AdminDashboard Component
 * Admin dashboard with sidebar navigation and content editor
 */
const AdminDashboard = () => {
  const [selectedCategory, setSelectedCategory] = useState('products');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Category menu items
  const categories = [
    {
      id: 'products',
      name: '제품 관리',
      nameEn: 'Product Management',
      icon: FiPackage,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100'
    },
    {
      id: 'company',
      name: '회사 소개 편집',
      nameEn: 'Company Info',
      icon: FiFileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      hoverBg: 'hover:bg-green-100'
    },
    {
      id: 'contact',
      name: '연락처 수정',
      nameEn: 'Contact Info',
      icon: FiMail,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100'
    },
    {
      id: 'images',
      name: '이미지 URL 변경',
      nameEn: 'Image URLs',
      icon: FiImage,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      hoverBg: 'hover:bg-orange-100'
    }
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? 'w-64' : 'w-0'
        } bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">
            Admin
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            콘텐츠 관리 시스템
          </p>
        </div>

        {/* Category Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                selectedCategory === category.id
                  ? `${category.bgColor} ${category.color} font-semibold`
                  : `text-gray-700 ${category.hoverBg}`
              }`}
            >
              <category.icon className="w-5 h-5 flex-shrink-0" />
              <div className="text-left flex-1">
                <div className="text-sm">{category.name}</div>
                <div className="text-xs opacity-70">{category.nameEn}</div>
              </div>
            </button>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            UI Mockup - No Backend
          </p>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Sidebar Toggle Button */}
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Toggle Sidebar"
            >
              {isSidebarOpen ? (
                <FiX className="w-6 h-6 text-gray-700" />
              ) : (
                <FiMenu className="w-6 h-6 text-gray-700" />
              )}
            </button>

            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {categories.find((c) => c.id === selectedCategory)?.name}
              </h2>
              <p className="text-sm text-gray-600">
                {categories.find((c) => c.id === selectedCategory)?.nameEn}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">관리자 모드</span>
            <Link
              to="/"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <FiHome className="w-4 h-4" />
              <span>홈페이지</span>
            </Link>
          </div>
        </header>

        {/* Content Editor Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <ContentEditor category={selectedCategory} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
